import uuid from 'uuid';
import moment from 'moment';
import { exception } from 'circe/logger';
import * as I18n from 'circe/I18n';
import invert from 'lodash/invert';
import { validatePhoneNumber } from 'circe/forms/validation';
import { CSV_HEADERS, CSV_REQUIRED_HEADERS } from './constants';

const LIMIT = 350;

const getLocationFromAddress = (address, type) => (address.address_components
  ? address.address_components.find((component) => component.types.indexOf(type) !== -1)
  : null);

export const morphFormAddressToRequestPayload = (address) => {
  address = address[0];

  const city = getLocationFromAddress(address, 'locality');
  const country = getLocationFromAddress(address, 'country');
  return {
    address: address.formatted_address,
    city: city ? city.long_name : '',
    coordinate: {
      latitude:
        typeof address.geometry.location.lat === 'function'
          ? address.geometry.location.lat()
          : address.geometry.location.lat,
      longitude:
        typeof address.geometry.location.lng === 'function'
          ? address.geometry.location.lng()
          : address.geometry.location.lng,
    },
    country: country ? country.long_name : '',
    description: `Batch Shipment ${moment().format('YYYY-MM-DD')}`,
    identifier: uuid.v4(),
  };
};

export const csvFileToObjets = (data) => {
  if (!data || data.length < 2) return [];

  const [header, ...allData] = data;
  const output = [];
  const headers = Object.keys(CSV_HEADERS);
  for (const row of allData) {
    const temp = {};

    row.forEach((rowItem, i) => {
      try {
        temp[headers[i]] = rowItem;
      } catch (e) {
        exception(e);
      }
    });

    output.push(temp);
  }
  return output;
};

export const validateCsvHeader = (headers = []) => {
  const errors = [];
  for (const header of CSV_REQUIRED_HEADERS) {
    if (!headers.includes(header)) {
      errors.push({
        type: 'MISSING_HEADER',
        message: I18n.str('missingHeader', '{header} Header does not exist ', {
          header,
        }),
      });
    }
  }
  return errors;
};

/**
 *
 * @param {
 *
 We have additional validations on the columns values and format:
 [Customer Phone, Item Weight (kg), Item Length (cm), Item Width (cm), Item Height (cm), COD Amount, Longitude, Latitude, Shipment Value]
 */
export const validateCsvRows = (input) => {
  const rows = [...input];
  const invertedValues = invert(CSV_HEADERS);
  if (rows.length > 1) {
    const lastRow = rows[rows.length - 1];
    let isAllEmpty = true;
    for (const key of Object.keys(lastRow)) {
      if (lastRow[key]) {
        isAllEmpty = false;
        break;
      }
    }
    if (isAllEmpty) {
      rows.pop();
    }
  }
  const errors = [];
  const requiredFields = [
    invertedValues['Customer Phone'],
    invertedValues['Item Weight (kg)'],
    invertedValues['Item Length (cm)'],
    invertedValues['Item Width (cm)'],
    invertedValues['Item Height (cm)'],
    invertedValues['COD Amount'],
    invertedValues.Longitude,
    invertedValues.Latitude,
    invertedValues['Shipment Value'],
  ];

  let i = 0;
  for (const row of rows) {
    for (const header of requiredFields) {
      let value = row[header];
      if (header === 'LATITUDE' || header === 'LONGITUDE') {
        if ((value || value === 0) && isNaN(Number(value).toFixed())) {
          errors.push({
            type: 'ERROR_VALUE',
            message: I18n.str('missingRowValue', '{header} ({value}) is not a number', {
              header: CSV_HEADERS[header],
              value,
            }),
            row: i,
          });
        }
      } else if (header === 'PHONE') {
        if (typeof value === 'undefined' || value === null || value === '') {
          errors.push({
            type: 'REQUIRED_VALUE',
            message: I18n.str('missingRowValue', '{header} is required ', {
              header: CSV_HEADERS[header],
            }),
            row: i,
          });
        } else {
          const regex = '/^+((?:9[679]|8[035789]|6[789]|5[90]|42|3[578]|2[1-689])|9[0-58]|8[1246]|6[0-6]|5[1-8]|4[013-9]|3[0-469]|2[70]|7|1)(?:W*d){0,13}d$/';

          if (value.length === 9) {
            value = `0${value}`;
          }

          // const isValidPhone = value.test(regex);
          if (!validatePhoneNumber(value)) {
            errors.push({
              type: 'ERROR_VALUE',
              message: I18n.str('missingRowValue', '{header} ({value}) is not a phone number', {
                header: CSV_HEADERS[header],
                value,
              }),
              row: i,
            });
          }
        }
      } else if (typeof value === 'undefined' || value === null || value === '') {
        errors.push({
          type: 'REQUIRED_VALUE',
          message: I18n.str('missingRowValue', '{header} is required ', {
            header: CSV_HEADERS[header],
          }),
          row: i,
        });
      } else if ((value || value === 0) && isNaN(Number(value).toFixed())) {
        errors.push({
          type: 'ERROR_VALUE',
          message: I18n.str('missingRowValue', '{header} ({value}) is not a number', {
            header: CSV_HEADERS[header],
            value,
          }),
          row: i,
        });
      }
    }
    i++;
  }
  return errors;
};

export const validateCsvData = (header, rows) => {
  const errors = [];
  if (rows.length > LIMIT) {
    errors.push({
      type: 'MAXIMUM_NUMBER',
      message: I18n.str('maximumNumberOfRows', 'Maximum allowed number of rows is {limit}', {
        limit: LIMIT,
      }),
    });
  }
  const headerErrors = validateCsvHeader(header);
  const rowsErrors = validateCsvRows(rows);

  return [...headerErrors, ...rowsErrors];
};
